import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Checkbox from '@/components/ui/Checkbox';

// Mock data
const mockBook = {
  id: 1,
  isbn: '978-2-123-456-7',
  title: 'Le Petit Prince',
  subtitle: 'Édition illustrée',
  language: 'fr-CA',
  status: 'PENDING',
  version: 1,
  publisher_id: 1,
  publisher_name: 'Éditions Kampus',
  cover_url: 'https://placehold.co/200x300/4AC3C3/white?text=📕',
  created_at: '2024-01-15T10:00:00Z',
};

const workflowSteps = [
  { name: 'Ingested', status: 'completed' },
  { name: 'Transformed', status: 'completed' },
  { name: 'Validated', status: 'current' },
  { name: 'Published', status: 'pending' },
  { name: 'Pushed', status: 'pending' },
];

const platforms = [
  { id: 'apple', name: 'Apple Books', status: 'Not sent', selected: true },
  { id: 'google', name: 'Google Play', status: 'Not sent', selected: true },
  { id: 'spotify', name: 'Spotify', status: 'Not sent', selected: true },
  { id: 'sftp', name: 'SFTP Export', status: '--', selected: false },
];

const events = [
  { timestamp: '2024-01-15T10:30:00Z', type: 'info', message: 'Book created' },
  { timestamp: '2024-01-15T10:31:00Z', type: 'info', message: 'Metadata validated' },
  { timestamp: '2024-01-15T10:32:00Z', type: 'info', message: 'Awaiting approval' },
];

function getStepStyle(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-success-500 border-success-500 text-white';
    case 'current':
      return 'bg-primary-500 border-primary-500 text-white animate-pulse';
    case 'pending':
      return 'bg-white border-slate-300 text-slate-400';
    default:
      return 'bg-white border-slate-300 text-slate-400';
  }
}

export default function DistBookDetailPage() {
  const { id: _id } = useParams();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/distribution/books"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Books
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Cover Image */}
        <div className="flex-shrink-0">
          <img
            src={mockBook.cover_url}
            alt={mockBook.title}
            className="w-40 h-60 rounded-2xl object-cover shadow-lg"
          />
        </div>

        {/* Book Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{mockBook.title}</h1>
              {mockBook.subtitle && (
                <p className="text-lg text-slate-500 mt-1">{mockBook.subtitle}</p>
              )}
              <p className="font-mono text-sm text-slate-400 mt-2">ISBN: {mockBook.isbn}</p>
            </div>
            <Badge variant="warning" size="lg">⏳ Pending Approval</Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div>
              <p className="text-sm text-slate-500">Publisher</p>
              <p className="font-medium text-slate-800">{mockBook.publisher_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Language</p>
              <p className="font-medium text-slate-800">{mockBook.language}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Version</p>
              <p className="font-medium text-slate-800">v{mockBook.version}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Created</p>
              <p className="font-medium text-slate-800">
                {new Date(mockBook.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Status */}
      <Card>
        <CardHeader title="Workflow Status" />
        <CardBody>
          <div className="flex items-center justify-between">
            {workflowSteps.map((step, index) => (
              <div key={step.name} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${getStepStyle(
                      step.status
                    )}`}
                  >
                    {step.status === 'completed' ? (
                      <CheckIcon className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-slate-700 mt-2">{step.name}</p>
                </div>
                {index < workflowSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      step.status === 'completed' ? 'bg-success-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Platform Delivery */}
      <Card>
        <CardHeader title="Platform Delivery" subtitle="Select platforms to publish to" />
        <CardBody>
          <div className="space-y-3">
            {platforms.map((platform) => (
              <div
                key={platform.id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
              >
                <Checkbox
                  label={platform.name}
                  checked={platform.selected}
                  onChange={() => {}}
                />
                <Badge variant={platform.status === 'Not sent' ? 'warning' : 'slate'}>
                  {platform.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Event History */}
      <Card>
        <CardHeader title="Event History" />
        <CardBody>
          <div className="space-y-3">
            {events.map((event, index) => (
              <div key={index} className="flex items-start gap-4">
                <span className="text-sm text-slate-400 w-40">
                  {new Date(event.timestamp).toLocaleString()}
                </span>
                <div className="w-2 h-2 mt-1.5 rounded-full bg-primary-400" />
                <p className="text-slate-700">{event.message}</p>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button leftIcon={<CheckIcon className="w-4 h-4" />}>
          Approve & Publish
        </Button>
        <Button variant="outline" leftIcon={<PencilIcon className="w-4 h-4" />}>
          Edit
        </Button>
        <Button variant="danger" leftIcon={<TrashIcon className="w-4 h-4" />}>
          Delete
        </Button>
      </div>
    </div>
  );
}

